"use client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { loginUser, useAuthDispatch } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import { cn } from "@/lib/utils";
import { Storage } from "@/services/storage-services";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeClosed, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";

export const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address!" }),
  password: z.string().nonempty(),
});

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const location = useLocation();
  const [searchParams] = useSearchParams()
  const next = searchParams.get("next") || "/"

  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAuthDispatch();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const res = await loginUser(dispatch, data);
      form.reset();

      if (res?.data?.data?.twoFactorAuthentication === false) {
        Storage.setItem("businessId", res?.data?.data?.teams[0]?.businessId);
        Storage.setItem("selectedMerchant", res?.data?.data?.teams[0]);
        Storage.setItem("env", res?.data?.data?.teams[0]?.env);
        if (next !== '/') {
          navigate(`${next}`);
        } else {
          navigate("/dashboard");
        }
        ToastMessage(type.SUCCESS, "Welcome, you are successfully logged in");
      } else if (res?.data?.data?.twoFactorAuthentication) {

        // location?.state?.from?.pathname
        navigate("/2fa-auth", { state: { uri: `${next}` } });
        // }
      }
    } catch (error: any) {
      ToastMessage(type.ERROR, error.response.data.message);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="w-full lg:w-[542px] p-6">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-medium">Admin Login</CardTitle>
          <CardDescription className="">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
              <div className="grid gap-5">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <div
                          className={cn(
                            "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                            form.formState.errors?.email === undefined
                              ? "border-input-border"
                              : "border-destructive"
                          )}
                        >
                          <div className="flex flex-col w-full">
                            <FormLabel className="text-xs">Email</FormLabel>

                            <FormControl>
                              <Input
                                type="email"
                                className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <div className="w-4 h-4 text-muted-foreground">
                            <Mail className="w-5 h-5" />
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <div className="flex flex-col gap-3">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <div
                            className={cn(
                              "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                              form.formState.errors?.password === undefined
                                ? "border-input-border"
                                : "border-destructive"
                            )}
                          >
                            <div className="flex flex-col w-full">
                              <FormLabel className="text-xs">
                                Password
                              </FormLabel>

                              <FormControl>
                                <Input
                                  type={!show ? "password" : "text"}
                                  placeholder=""
                                  className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                  {...field}
                                />
                              </FormControl>
                            </div>
                            <div
                              className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                              onClick={() => setShow(!show)}
                            >
                              {!show ? (
                                <EyeClosed />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </div>
                          </div>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                  <Link
                    to="forgot-password"
                    className="ml-auto text-sm underline-offset-4 hover:underline text-primary"
                  >
                    Forgot your password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  className="w-full bg-gradient-to-r from-primary to-primary-2 h-14 rounded-[10px] p-[10px]  ring-transparent focus-visible:ring-transparent"
                  disabled={
                    !form.formState.isDirty ||
                    !form.formState.isValid ||
                    form.formState.isSubmitting
                  }
                >
                  {form.formState.isSubmitting && (
                    <Loader2 className="text-white animate-spin" />
                  )}
                  Login
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      
    </div>
  );
}
